
'use client';

import { useState } from 'react';
import { useAuth, useFirestore, useUser } from '@/firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { PenSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { initiateAnonymousSignIn } from '@/firebase/non-blocking-login';

const LOCAL_STORAGE_STATS_KEY = 'moderationStats';
const WEEKLY_LIMIT = 50;
const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

interface ModerationStats {
  weekStart: number;
  count: number;
}

interface ReviewFormProps {
    salonId: string;
    province: string;
    labels?: {
        quality: string;
        handling: string;
        service: string;
    };
}

export default function ReviewForm({ salonId, province, labels }: ReviewFormProps) {
  const { toast } = useToast();
  const firestore = useFirestore();
  const auth = useAuth();
  const { user, isUserLoading } = useUser();
  
  const [isOpen, setIsOpen] = useState(false);
  const [authorName, setAuthorName] = useState('');
  const [scoreQuality, setScoreQuality] = useState(5);
  const [scoreHandling, setScoreHandling] = useState(5);
  const [scoreService, setScoreService] = useState(5);
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default labels for Grooming, but can be overridden
  const displayLabels = labels || {
    quality: "Kwaliteit van de trimbeurt",
    handling: "Omgang met de hond",
    service: "Klantenservice en vriendelijkheid"
  };

  const handleOpenForm = async () => {
    if (!user && auth) {
      initiateAnonymousSignIn(auth);
    }
    setIsOpen(true);
  };
  
  const resetForm = () => {
    setAuthorName('');
    setScoreQuality(5);
    setScoreHandling(5);
    setScoreService(5);
    setText('');
    setIsSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !firestore) {
      toast({ variant: "destructive", title: "Fout", description: "Geen verbinding." });
      return;
    }
    setIsSubmitting(true);

    const finalAuthorName = user?.displayName || authorName;

    if (!finalAuthorName || !text) {
        toast({ variant: "destructive", title: "Fout", description: "Vul alle velden in." });
        setIsSubmitting(false);
        return;
    }

    const now = Date.now();
    const statsString = localStorage.getItem(LOCAL_STORAGE_STATS_KEY);
    let stats: ModerationStats = statsString ? JSON.parse(statsString) : { weekStart: now, count: 0 };
    
    if (now - stats.weekStart > WEEK_IN_MS) stats = { weekStart: now, count: 0 };

    if (stats.count >= WEEKLY_LIMIT) {
      window.location.href = `mailto:admin@kzoek.com?subject=Recensie ${salonId}&body=${encodeURIComponent(text)}`;
      setIsSubmitting(false);
      setIsOpen(false);
      toast({ title: "Limiet bereikt", description: "Uw e-mail wordt geopend." });
    } else {
      try {
        await addDoc(collection(firestore, 'reviews'), {
          salonId,
          province,
          userId: user.uid,
          authorName: finalAuthorName,
          isAnonymous: user.isAnonymous || !user.displayName,
          scoreQuality,
          scoreHandling,
          scoreService,
          text,
          createdAt: serverTimestamp(),
          status: 'pending',
        });
        toast({ title: "Bedankt!", description: "Recensie ontvangen." });
        setIsOpen(false);
        resetForm();
      } catch (error) {
        toast({ variant: "destructive", title: "Fout", description: "Indienen mislukt." });
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button onClick={handleOpenForm}>
          <PenSquare className="mr-2 h-4 w-4" />
          Schrijf een recensie
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Uw recensie</DialogTitle>
            <DialogDescription>Deel uw ervaring met de gemeenschap.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-6">
            {!user?.displayName && (
              <div className="space-y-2">
                <Label htmlFor="authorName">Uw naam</Label>
                <Input id="authorName" value={authorName} onChange={(e) => setAuthorName(e.target.value)} required />
              </div>
            )}
            <div className="space-y-4">
                <ScoreSlider label={displayLabels.quality} value={scoreQuality} onChange={setScoreQuality} />
                <ScoreSlider label={displayLabels.handling} value={scoreHandling} onChange={setScoreHandling} />
                <ScoreSlider label={displayLabels.service} value={scoreService} onChange={setScoreService} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="text">Uw ervaring</Label>
              <Textarea id="text" value={text} onChange={(e) => setText(e.target.value)} required />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Bezig...' : 'Indienen'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ScoreSlider({ label, value, onChange }: { label: string, value: number, onChange: (v: number) => void }) {
    return (
        <div className="space-y-2">
            <Label>{label}</Label>
            <div className="flex items-center gap-4">
                <Slider value={[value]} onValueChange={(val) => onChange(val[0])} min={1} max={10} step={1} />
                <span className="font-bold w-8">{value}</span>
            </div>
        </div>
    );
}
